import { UploadSimpleIcon } from '@phosphor-icons/react';
import { useState } from 'react';

import { BaseButton } from '../../../components/button/BaseButton';
import { Pill } from '../../../components/pill/Pill';
import { useTranslation } from '../../../contexts/translation-context';
import {
  IncomingDocumentRejectionReasonToTranslationKey,
  OrderDocumentTypeToTranslationKey,
  RequiredDocument,
  RequiredDocumentStatusToColor,
  RequiredDocumentStatusToIcon,
  isCarrierUploadableDocumentType,
} from '../../order/utils/document-validation';
import { CarrierDocumentUploadDialog } from './CarrierDocumentUploadDialog';

interface Props {
  carrierId: number;
  orderId: string;
  /** The customer's checklist for this order, in the order it should be shown. */
  requiredDocuments: RequiredDocument[];
  disableUpload?: boolean;
  onUploaded: () => Promise<void>;
}

/**
 * The "required documents" cell of the carrier order list: one badge per
 * required document carrying its own state, and — only while something is
 * still outstanding — a single upload button on the right.
 *
 * The badges are the status: green needs no further words, so a reason is
 * spelled out only when the document is refused or still being checked. The
 * upload button asks for no document type; a carrier drops in whatever they
 * have and the analyzer splits the upload and files each part under the right
 * type.
 */
export const CarrierPortalRequiredDocsCell: React.FC<Props> = ({
  carrierId,
  orderId,
  requiredDocuments,
  disableUpload,
  onUploaded,
}) => {
  const { i18n } = useTranslation();
  const [isUploadOpen, setIsUploadOpen] = useState(false);

  // Only the documents still expected: the dialog declares the first of these as
  // the upload's type, so a document that is already satisfied must not be in
  // the list or the upload gets filed under the wrong type.
  const uploadableTypes = requiredDocuments
    .filter((d) => d.status !== 'satisfied')
    .map((d) => d.type)
    .filter(isCarrierUploadableDocumentType);
  // Nothing left to hand in means nothing left to upload — a fully approved
  // order offers no way to add more documents.
  const canUpload = uploadableTypes.length > 0 && !disableUpload;

  const reasonFor = (doc: RequiredDocument): string | null => {
    if (doc.status === 'pending') return i18n('carrierPortal.orders.docReasonInReview');
    if (doc.status !== 'rejected') return null;
    if (!doc.rejectionReasons?.length) return i18n('carrierPortal.orders.docReasonRejected');
    return doc.rejectionReasons
      .map((reason) => i18n(IncomingDocumentRejectionReasonToTranslationKey[reason]))
      .join(', ');
  };

  return (
    <div className="flex w-full items-center gap-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        {requiredDocuments.map((doc) => {
          const StatusIcon = RequiredDocumentStatusToIcon[doc.status];
          const reason = reasonFor(doc);

          return (
            <Pill key={doc.type} color={RequiredDocumentStatusToColor[doc.status]}>
              <StatusIcon className="h-[11px] w-[11px] shrink-0" weight="bold" />
              {i18n(OrderDocumentTypeToTranslationKey[doc.type])}
              {doc.count && ` ${doc.count.received}/${doc.count.required}`}
              {reason && <span className="font-normal opacity-75">· {reason}</span>}
            </Pill>
          );
        })}
      </div>

      {canUpload && (
        <>
          <BaseButton
            className="ml-auto"
            color="secondary"
            size={8}
            iconLeft={<UploadSimpleIcon className="button-icon" />}
            onClick={() => setIsUploadOpen(true)}
          >
            {i18n('carrierPortal.orders.uploadRequiredDoc')}
          </BaseButton>

          <CarrierDocumentUploadDialog
            carrierId={carrierId}
            orderId={orderId}
            types={isUploadOpen ? uploadableTypes : null}
            onClose={() => setIsUploadOpen(false)}
            onUploaded={onUploaded}
          />
        </>
      )}
    </div>
  );
};
